import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AppNav from "@/components/AppNav";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Activity, Users, Loader2, RefreshCw, Eye, EyeOff, KeyRound, LayoutDashboard, Compass } from "lucide-react";
import { toast } from "sonner";
import { isAnalyticsExcluded, setAnalyticsExcluded } from "@/lib/siteAnalytics";
import { TrafficPanel } from "@/pages/analytics/TrafficPanel";
import { GuideActivityPanel } from "@/pages/analytics/GuideActivityPanel";
import { ExplorerActivityPanel } from "@/pages/analytics/ExplorerActivityPanel";
import { OverviewPanel } from "@/pages/analytics/OverviewPanel";
import { InviteCodesPanel } from "@/pages/analytics/InviteCodesPanel";

export default function AdminAnalytics() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [days, setDays] = useState(30);
  const [traffic, setTraffic] = useState(null);
  const [guides, setGuides] = useState(null);
  const [explorers, setExplorers] = useState(null);
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [forbidden, setForbidden] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [t, g, e, o] = await Promise.all([
        api.get(`/admin/analytics/traffic?days=${days}`),
        api.get("/admin/analytics/guides"),
        api.get("/admin/analytics/explorers"),
        api.get(`/admin/analytics/overview?days=${days}`),
      ]);
      setTraffic(t.data); setGuides(g.data); setExplorers(e.data); setOverview(o.data);
    } catch (e) {
      if (e?.response?.status === 403) setForbidden(true);
      else toast.error("Could not load analytics.");
    } finally { setLoading(false); }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  if (!user?.is_owner || forbidden) {
    return (
      <div className="min-h-screen"><AppNav />
        <div className="max-w-3xl mx-auto px-6 py-20 text-center" data-testid="analytics-forbidden">
          <p className="font-display text-3xl">This page is for the HorizonQuest owner.</p>
          <Button className="mt-6" variant="outline" onClick={() => navigate("/guide")}>Back to the console</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="admin-analytics-page">
      <AppNav />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <button data-testid="analytics-back-btn" onClick={() => navigate("/guide")} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6">
          <ArrowLeft className="w-4 h-4" /> Back to the console
        </button>
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8 hq-fade-up">
          <div>
            <p className="text-xs uppercase tracking-widest font-mono-data text-[#22D3EE]">Owner analytics</p>
            <h1 className="font-display text-4xl sm:text-5xl tracking-tight">Who's sailing with us</h1>
          </div>
          <div className="flex items-center gap-2">
            {[7, 30, 90].map((d) => (
              <Button key={d} size="sm" data-testid={`range-${d}d-btn`} variant={days === d ? "default" : "outline"} onClick={() => setDays(d)}
                className={days === d ? "bg-[#22D3EE] text-[#04121f] hover:bg-[#67E8F9]" : ""}>Last {d}d</Button>
            ))}
            <Button size="sm" variant="outline" data-testid="refresh-analytics-btn" onClick={load} disabled={loading}><RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /></Button>
            <ExcludeToggle />
          </div>
        </div>

        <Tabs defaultValue="overview">
          <TabsList className="bg-white/5 border border-white/10 flex-wrap h-auto">
            <TabsTrigger value="overview" data-testid="tab-overview"><LayoutDashboard className="w-4 h-4 mr-2" />Overview</TabsTrigger>
            <TabsTrigger value="guides" data-testid="tab-guides"><Users className="w-4 h-4 mr-2" />Guide Activity</TabsTrigger>
            <TabsTrigger value="explorers" data-testid="tab-explorers"><Compass className="w-4 h-4 mr-2" />Explorer Activity</TabsTrigger>
            <TabsTrigger value="traffic" data-testid="tab-traffic"><Activity className="w-4 h-4 mr-2" />Site Traffic</TabsTrigger>
            <TabsTrigger value="invites" data-testid="tab-invites"><KeyRound className="w-4 h-4 mr-2" />Invite Codes</TabsTrigger>
          </TabsList>
          <TabsContent value="invites" className="mt-6"><InviteCodesPanel /></TabsContent>
          <TabsContent value="overview" className="mt-6">
            {!overview ? <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div> : <OverviewPanel data={overview} days={days} />}
          </TabsContent>
          <TabsContent value="traffic" className="mt-6">
            {!traffic ? <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div> : <TrafficPanel traffic={traffic} days={days} />}
          </TabsContent>
          <TabsContent value="guides" className="mt-6">
            {!guides ? <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div> : <GuideActivityPanel data={guides} onChanged={load} />}
          </TabsContent>
          <TabsContent value="explorers" className="mt-6">
            {!explorers ? <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div> : <ExplorerActivityPanel data={explorers} />}
          </TabsContent>
        </Tabs>
        <p className="text-xs text-muted-foreground text-center pt-8">Your own page views are excluded automatically. Visitors are identified by an anonymous browser id — no personal data is collected.</p>
      </div>
    </div>
  );
}

function ExcludeToggle() {
  const [excluded, setExcluded] = useState(isAnalyticsExcluded());
  const toggle = () => {
    const next = !excluded;
    setAnalyticsExcluded(next); setExcluded(next);
    toast[next ? "success" : "info"](next ? "This browser is no longer counted in site stats." : "This browser is counted again.");
  };
  return (
    <Button size="sm" variant="outline" data-testid="analytics-exclude-toggle" onClick={toggle} className={excluded ? "border-[#34D399]/50 text-[#34D399]" : ""}>
      {excluded ? <EyeOff className="w-4 h-4 mr-1.5" /> : <Eye className="w-4 h-4 mr-1.5" />}{excluded ? "Browser ignored" : "Ignore my browser"}
    </Button>
  );
}
